import z from 'zod';

import { DataResponseSchema } from './index.js';
import { DataTypePropertySchema } from './metadata.js';

export const MetricsIntegrationSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const MetricsIntegrationsResponseSchema = z.object({
  data: DataResponseSchema(MetricsIntegrationSchema),
});

export const IntegrationQuerySchema = z.object({
  dataSource: z.string().optional(),
  companyId: z.string().or(z.number()).optional(),
});

export const ConversionMetricsSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const ConversionQuerySchema = z.object({
  dataSource: z.string().optional(),
  companyId: z.string().or(z.number()).optional(),
  integrationId: z.string().optional(),
});

export const ConversionMetricsResponseSchema = z.object({
  data: DataResponseSchema(ConversionMetricsSchema),
});

export const MetricSchema = z.object({
  id: z.string(),
  fieldDefinitionId: z.string(),
  group: z.string().prefault('metrics'),
  dataType: z.string(),
  name: z.string(),
  companyId: z.number().nullish(),
  isSystem: z.boolean(),
  description: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  integrationId: z.string().nullish(),
  conversionId: z.string().nullish(),
  conversionName: z.string().nullish(),
  pivotMetricId: z.string().nullish(),
  dataSource: z.string().nullish(),
  metricType: z.enum(['default', 'integrated', 'custom']),
  formula: z.string().nullish(),
  useKpi: z.boolean(),
  isFinancial: z.boolean(),
  context: z.string().optional(),
  globalId: z.number().nullish(),
  createdBy: z.number().nullish(),
  createdAt: z.string(),
  updatedBy: z.number().nullish(),
  updatedAt: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  updatedByFullName: z.string().nullish(),
  globalCreatedAt: z.string().nullish(),
  globalUpdatedBy: z.number().nullish(),
  globalUpdatedAt: z.string().nullish(),
  globalUpdatedByFullName: z.string().nullish(),
  globalCreatedByFullName: z.string().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const ResponseDataMetricSchema = z.object({
  data: DataResponseSchema(MetricSchema),
  error: z.any().nullish(),
});

export const ListMetricsQuerySchema = z.object({
  companyId: z.string().or(z.number()).optional(),
  context: z.string().optional(),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().optional(),
  ordering: z.string().optional(),
  labelOrName: z.string().optional(),
  containsLabelOrName: z.string().or(z.boolean()).optional(),
  description: z.string().optional(),
  containsDescription: z.string().or(z.boolean()).optional(),
  dataTypes: z.array(z.string()).nullish(),
  dataSources: z.array(z.string()).nullish(),
  metricTypes: z.array(z.string()).nullish(),
  formulaMetrics: z.array(z.string()).nullish(),
});

export const CreateMetricRequestSchema = z.object({
  group: z.string().prefault('metrics'),
  dataType: z.string(),
  name: z.string().min(1).max(255),
  companyId: z.string().or(z.number()).nullable(),
  isSystem: z.boolean().prefault(false),
  description: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  integrationId: z.string().nullable(),
  conversionId: z.string().or(z.number()).nullable(),
  conversionName: z.string().nullish(),
  dataSource: z.string().nullish(),
  metricType: z.enum(['default', 'integrated', 'custom']),
  formula: z.string().nullish(),
  useKpi: z.boolean().prefault(false),
  isFinancial: z.boolean().prefault(false),
  context: z.string().prefault('reports'),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const UpdateMetricRequestSchema = z.object({
  name: z.string().max(255).nullish(),
  description: z.string().nullish(),
  useKpi: z.boolean().optional(),
  formula: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  dataType: z.string().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const MetricOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  fieldDefinitionId: z.string(),
  dataType: z.string().optional(),
});

export const MetricOptionResponseSchema = z.object({
  data: DataResponseSchema(MetricOptionSchema),
});

export const ResponseDataMetricOptionsSchema = z.object({
  data: MetricOptionResponseSchema.nullish(),
  error: z.any().nullish(),
});

export const MetricOptionsQuerySchema = z.object({
  context: z.string().optional(),
  companyId: z.string().or(z.number()).optional(),
});

export type MetricType = z.infer<typeof MetricSchema>;
export enum MetricTypeEnum {
  default = 'default',
  integrated = 'integrated',
  custom = 'custom',
}
export type ResponseDataMetricType = z.infer<typeof ResponseDataMetricSchema>;
export type CreateMetricRequestType = z.infer<typeof CreateMetricRequestSchema>;
